import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import LandscapeIcon from '@mui/icons-material/Landscape';
import CropPortraitIcon from '@mui/icons-material/CropPortrait';

export type PaperSize = 'A4' | 'A3' | 'A2' | 'A1' | 'A0';
export type Orientation = 'landscape' | 'portrait';

export interface PaperDimensions {
  width: number;  // mm
  height: number; // mm
  pixelWidth: number;  // at 300 DPI
  pixelHeight: number; // at 300 DPI
}

// Paper sizes in mm (portrait orientation)
export const PAPER_SIZES: Record<PaperSize, { width: number; height: number; description: string }> = {
  A4: { width: 210, height: 297, description: 'Reports, permits, method statements' },
  A3: { width: 297, height: 420, description: 'Most common for TMPs' },
  A2: { width: 420, height: 594, description: 'Detailed intersection layouts' },
  A1: { width: 594, height: 841, description: 'Large road corridors, complex works' },
  A0: { width: 841, height: 1189, description: 'Major infrastructure / overview plans' },
};

// Convert mm to pixels at 300 DPI
const MM_TO_PIXELS_300DPI = 300 / 25.4; // 11.811 pixels per mm

export const getPaperDimensions = (size: PaperSize, orientation: Orientation): PaperDimensions => {
  const paper = PAPER_SIZES[size];
  const isLandscape = orientation === 'landscape';
  
  const widthMm = isLandscape ? paper.height : paper.width;
  const heightMm = isLandscape ? paper.width : paper.height;
  
  return {
    width: widthMm,
    height: heightMm,
    pixelWidth: Math.round(widthMm * MM_TO_PIXELS_300DPI),
    pixelHeight: Math.round(heightMm * MM_TO_PIXELS_300DPI),
  };
};

export interface PrintSettingsProps {
  paperSize: PaperSize;
  orientation: Orientation;
  onPaperSizeChange: (size: PaperSize) => void;
  onOrientationChange: (orientation: Orientation) => void;
}

export const PrintSettings: React.FC<PrintSettingsProps> = ({
  paperSize,
  orientation,
  onPaperSizeChange,
  onOrientationChange,
}) => {
  const dimensions = getPaperDimensions(paperSize, orientation);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>
        Paper Settings
      </Typography>

      {/* Paper Size Selector */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="paper-size-label">Paper Size</InputLabel>
        <Select
          labelId="paper-size-label"
          value={paperSize}
          label="Paper Size"
          onChange={(e) => onPaperSizeChange(e.target.value as PaperSize)}
        >
          {(Object.keys(PAPER_SIZES) as PaperSize[]).map((size) => (
            <MenuItem key={size} value={size}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {size}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({PAPER_SIZES[size].width} × {PAPER_SIZES[size].height} mm)
                  </Typography>
                </Box>
                {size === 'A3' && (
                  <Chip
                    label="RECOMMENDED"
                    size="small"
                    color="success"
                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Orientation Toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Orientation:
        </Typography>
        <ToggleButtonGroup
          value={orientation}
          exclusive
          onChange={(_, value) => value && onOrientationChange(value)}
          size="small"
        >
          <ToggleButton value="landscape" sx={{ px: 2 }}>
            <LandscapeIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Landscape
          </ToggleButton>
          <ToggleButton value="portrait" sx={{ px: 2 }}>
            <CropPortraitIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Portrait
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Dimensions Preview */}
      <Box
        sx={{
          p: 1.5,
          borderRadius: 1,
          backgroundColor: 'action.hover',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Output dimensions:
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 500 }}>
          {dimensions.width} × {dimensions.height} mm ({dimensions.pixelWidth} × {dimensions.pixelHeight} px @ 300 DPI)
        </Typography>
      </Box>

      {/* Use case hint */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
        {PAPER_SIZES[paperSize].description}
      </Typography>
    </Box>
  );
};

export default PrintSettings;
